/**
 * AdminHealth — integrations dashboard at `/admin/health`.
 *
 * Reads `GET /api/admin/health` (server-side) and surfaces frontend-only
 * key presence (Paystack, Google Maps) inferred from `process.env`.
 *
 * No secrets are rendered — only a configured/ok flag per integration.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, AlertTriangle, XCircle, RefreshCw, Activity } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

const FRONTEND_INTEGRATIONS = [
    {
        key: "paystack",
        label: "Paystack (frontend)",
        configured: !!process.env.REACT_APP_PAYSTACK_PUBLIC_KEY,
        envVar: "REACT_APP_PAYSTACK_PUBLIC_KEY",
        hint: "Public key required to open the Paystack checkout iframe.",
    },
    {
        key: "google_maps",
        label: "Google Maps (frontend)",
        configured: !!process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        envVar: "REACT_APP_GOOGLE_MAPS_API_KEY",
        hint: "Required for Places autocomplete and Near-me geocoding.",
    },
    {
        key: "supabase_anon",
        label: "Supabase (frontend client)",
        configured:
            !!process.env.REACT_APP_SUPABASE_URL &&
            !!process.env.REACT_APP_SUPABASE_ANON_KEY,
        envVar: "REACT_APP_SUPABASE_URL + REACT_APP_SUPABASE_ANON_KEY",
        hint: "Browser-side Supabase client (auth + read mirroring).",
    },
    {
        key: "termii_proxy",
        label: "Termii proxy",
        configured: !!process.env.REACT_APP_TERMII_PROXY,
        envVar: "REACT_APP_TERMII_PROXY",
        hint: "Set to 1 so OTP requests route through the backend.",
    },
];

const BACKEND_LABELS = {
    mongo: "MongoDB",
    termii: "Termii (backend SMS)",
    supabase: "Supabase (admin / service role)",
    admin_secret: "Admin API secret",
};

const StatusBadge = ({ configured, ok }) => {
    if (configured && ok === true) {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30">
                <CheckCircle2 size={11} />
                Live
            </span>
        );
    }
    if (configured && ok === null) {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold bg-primary/10 text-primary border border-primary/30">
                <CheckCircle2 size={11} />
                Configured
            </span>
        );
    }
    if (configured && ok === false) {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold bg-destructive/10 text-destructive border border-destructive/30">
                <XCircle size={11} />
                Error
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/30">
            <AlertTriangle size={11} />
            Not set
        </span>
    );
};

const Row = ({ label, configured, ok, detail, hint }) => (
    <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-border last:border-b-0">
        <div className="min-w-0">
            <div className="text-[13.5px] font-medium truncate">{label}</div>
            <div className="text-[11.5px] text-muted-foreground mt-0.5">
                {detail || hint || "—"}
            </div>
        </div>
        <StatusBadge configured={configured} ok={ok} />
    </div>
);

const AdminHealth = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastRefresh, setLastRefresh] = useState(null);

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const { data: res } = await axios.get(`${API}/api/admin/health`, { timeout: 12000 });
            setData(res || {});
            setLastRefresh(new Date());
        } catch (err) {
            setError(err?.response?.data?.detail || err?.message || "Failed to load.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const integrations = data?.integrations || {};
    const counts = data?.counts || {};

    return (
        <div data-testid="admin-health" className="space-y-4">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h2 className="font-display text-[22px] font-semibold tracking-tight inline-flex items-center gap-2">
                        <Activity size={18} className="text-primary" />
                        Integrations health
                    </h2>
                    <p className="text-[13px] text-muted-foreground">
                        Quick status check for every external service this app talks to.
                        Refresh after rotating any env variable.
                    </p>
                </div>
                <button
                    type="button"
                    data-testid="admin-health-refresh"
                    onClick={load}
                    disabled={loading}
                    className="h-10 px-3 rounded-xl bg-secondary border border-border text-[12.5px] font-medium hover:bg-muted inline-flex items-center gap-1.5 disabled:opacity-50"
                >
                    <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
                    Refresh
                </button>
            </div>

            {error && (
                <div
                    data-testid="admin-health-error"
                    className="rounded-xl border border-destructive/40 bg-destructive/10 text-destructive text-[12.5px] font-medium px-4 py-3"
                >
                    {error}
                </div>
            )}

            <section className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="admin-health-backend">
                <div className="px-4 py-3 border-b border-border bg-muted/40">
                    <h3 className="font-display text-[14px] font-semibold">Backend integrations</h3>
                    <p className="text-[11.5px] text-muted-foreground">
                        Read from <code className="font-mono">/api/admin/health</code> — keys live on the server only.
                    </p>
                </div>
                {Object.keys(integrations).length === 0 && !loading && !error && (
                    <div className="px-4 py-6 text-center text-[12.5px] text-muted-foreground">
                        No integration data returned.
                    </div>
                )}
                {Object.entries(integrations).map(([key, val]) => (
                    <Row
                        key={key}
                        label={BACKEND_LABELS[key] || key}
                        configured={val.configured}
                        ok={val.ok}
                        detail={val.detail}
                    />
                ))}
            </section>

            <section className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="admin-health-frontend">
                <div className="px-4 py-3 border-b border-border bg-muted/40">
                    <h3 className="font-display text-[14px] font-semibold">Frontend integrations</h3>
                    <p className="text-[11.5px] text-muted-foreground">
                        Inferred from <code className="font-mono">process.env</code> at build time. Only key presence is shown — never the value.
                    </p>
                </div>
                {FRONTEND_INTEGRATIONS.map((it) => (
                    <Row
                        key={it.key}
                        label={it.label}
                        configured={it.configured}
                        ok={it.configured ? null : false}
                        detail={it.configured ? `Set via ${it.envVar}` : `Missing: ${it.envVar}`}
                        hint={it.hint}
                    />
                ))}
            </section>

            <section className="rounded-2xl border border-border bg-card p-4" data-testid="admin-health-counts">
                <h3 className="font-display text-[14px] font-semibold mb-2">Database counts</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                    {["users", "services", "events", "bookings", "transactions"].map((k) => (
                        <div
                            key={k}
                            className="rounded-xl bg-secondary border border-border p-3"
                            data-testid={`admin-health-count-${k}`}
                        >
                            <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">{k}</div>
                            <div className="font-display text-[18px] font-semibold mt-0.5 tabular-nums">
                                {counts[k] ?? "—"}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {lastRefresh && (
                <p className="text-[11px] text-muted-foreground text-right" data-testid="admin-health-last-refresh">
                    Last refreshed {lastRefresh.toLocaleTimeString()}
                </p>
            )}
        </div>
    );
};

export default AdminHealth;
